import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Checkbox } from '@lmn/design-system';
import { focusRing, srOnly } from '@/app/components/search/styles/mixins.ts';
import { pillStateStyles, type PillState } from '@/app/common/components/Pill/styled.ts';

export type TileVariant = 'inline' | 'stacked';

// A single-line tile is shorter than its min-height either way, so justify-content sets the vertical
// rhythm and the padding only guards a wrapped label from the border. Keep it at space100: at
// space150 a stacked tile's own content (20 icon + space50 + 24 label) plus padding and border
// overflows to 74px against the 72px design.
const variantStyles = {
  inline: css`
    min-height: 48px;
    ${({ theme }) => theme.kallisto.mixins.font('Title5')};
  `,
  stacked: css`
    flex-direction: column;
    min-height: 72px;
    ${({ theme }) => theme.kallisto.mixins.font('Body')};
  `,
};

const Tile = styled.span<PillState & { $variant: TileVariant }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.kallisto.space.space50};
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => theme.kallisto.space.space100};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius100};
  ${({ $variant }) => variantStyles[$variant]}
  ${pillStateStyles}
`;

// Visually hidden but real, so the tile stays a checkbox for AT and keyboard.
const TileCheckbox = styled(Checkbox)`
  padding: 0;
  width: 100%;
  /* No height here: this is the flex item, and a definite height would defeat the row's
     align-self: stretch. The chain below carries the stretched height down to the Tile, so tiles
     sharing a row keep one border height when a label wraps. */

  button {
    ${srOnly};
  }

  &:has(button:focus-visible) ${Tile} {
    ${focusRing};
  }

  /* The tile paints its own state, so the design system's label hover must not show through. */
  &:hover {
    background-color: transparent;
  }

  > span {
    display: flex;
    width: 100%;
    height: 100%;
    margin: 0 !important;
    background: transparent;

    [class*='LabelContent'],
    [class*='LabelText'] {
      width: 100%;
      height: 100%;
      min-width: 0;
    }
  }
`;

export type FilterTileProps = {
  id: string;
  ariaLabel?: string;
  variant: TileVariant;
  selected: boolean;
  disabled: boolean;
  onChange: () => void;
  children: ReactNode;
};

export const FilterTile = ({ id, ariaLabel, variant, selected, disabled, onChange, children }: FilterTileProps) => (
  <TileCheckbox
    checkbox={{
      id,
      ariaLabel,
      label: (
        <Tile $variant={variant} $selected={selected} $enabled={!disabled}>
          {children}
        </Tile>
      ),
    }}
    onChange={onChange}
    labelPosition="end"
    isChecked={selected}
    disabled={disabled}
    fullWidth={true}
  />
);
